/**
 * Glyph registry (Ken 2026-10-22). One named object per functional/graphical
 * icon in the app, so the art can be swapped in one place and every instance
 * updates. KEN-side for now (portable to MOVU later).
 *
 * Each glyph has: a `label` (human name — shown as the switcher text / tooltip,
 * changeable per tenant), a `filename` (placeholder reminder of today's icon,
 * to be overwritten by the real art), and `media` (a data URI; null until
 * Claude Desktop's art is pulled into the tenant's ui_config.glyphs[<name>]).
 *
 * Usage wraps the CURRENT icon as the fallback:
 *     <Glyph name="phases" size={10}><ChevronsRight size={10} /></Glyph>
 * When media is present we render it instead; otherwise the children (today's
 * lucide icon, or text) render unchanged — nothing breaks before art arrives.
 *
 * GLYPH_DEFAULTS below is the baseline label/filename for every glyph; a tenant
 * overrides any of it via ui_config.glyphs. The media ALWAYS comes from the
 * tenant config (there is no bundled art).
 */
import type { ReactNode } from 'react'
import { getGlyphEntry } from './uiConfig'

export type GlyphName =
  | 'phases' | 'tray' | 'chronology' | 'docs' | 'add_from_drive'
  | 'sort_alpha' | 'sort_clock' | 'ai_sparkle' | 'folder' | 'subfolder' | 'matter'
  | 'pane2_sort_dir' | 'move' | 'archive' | 'manage' | 'email' | 'doc'
  | 'search_clear' | 'collapse_sidebar' | 'view' | 'summary_slip'

// Baseline label + placeholder filename per glyph. Filenames describe TODAY's
// icon so Ken recognizes what each slot currently is before the art is replaced.
export const GLYPH_DEFAULTS: Record<GlyphName, { label: string; filename: string }> = {
  phases:          { label: 'Phases',          filename: 'double-chevron-right.png' },
  tray:            { label: 'Tray',            filename: 'stacked-layers.png' },
  chronology:      { label: 'Chronology',      filename: 'stackoflines.png' },
  docs:            { label: 'Docs',            filename: 'doc-lines.png' },
  add_from_drive:  { label: 'Add from Drive',  filename: 'file-plus.png' },
  sort_alpha:      { label: 'Sort A–Z',        filename: 'arrow-down-az.png' },
  sort_clock:      { label: 'Sort by activity',filename: 'clock.png' },
  ai_sparkle:      { label: 'AI',              filename: 'sparkle.png' },
  folder:          { label: 'Folder',          filename: 'folder.png' },
  subfolder:       { label: 'Subfolder',       filename: 'folder.png' },
  matter:          { label: 'Matter',          filename: 'expanding-wallet.png' },
  pane2_sort_dir:  { label: 'Sort direction',  filename: 'arrow-down.png' },
  move:            { label: 'Move',            filename: 'folder-input.png' },
  archive:         { label: 'Archive',         filename: 'archive-box.png' },
  manage:          { label: 'Manage',          filename: 'three-dots.png' },
  email:           { label: 'Email',           filename: 'envelope.png' },
  doc:             { label: 'Document',        filename: 'paperclip.png' },
  search_clear:    { label: 'Clear search',    filename: 'x.png' },
  collapse_sidebar:{ label: 'Collapse sidebar',filename: 'panel-left-close.png' },
  view:            { label: 'View',            filename: 'eye.png' },
  summary_slip:    { label: 'Summary slip',     filename: 'summary-slip.svg' },
}

/** The tenant-overridable label for a glyph (falls back to the baseline). */
export function glyphLabel(name: GlyphName): string {
  return getGlyphEntry(name)?.label || GLYPH_DEFAULTS[name].label
}

/**
 * Render a glyph. Resolution order:
 *   - entry.media === ''  → render NOTHING (an explicit "no glyph here" decision,
 *     distinct from an unset key that falls back to the current icon).
 *   - media is raw SVG markup (`<svg…>`) → inline it so `fill="currentColor"`
 *     inherits the row's text / hover / selected colour (an <img> would not).
 *   - media is a data: URI (png/jpeg/svg) → render as <img>.
 *   - otherwise → `children` (today's lucide icon / text), unchanged.
 */
export function Glyph({ name, children = null, size = 14, className }: {
  name: GlyphName
  children?: ReactNode
  size?: number
  className?: string
}) {
  const entry = getGlyphEntry(name)
  const media = entry?.media
  // Explicit empty string = "deliberately no glyph" (e.g. top-level folders).
  if (entry && media === '') return null
  if (typeof media === 'string' && media) {
    if (media.trimStart().toLowerCase().startsWith('<svg')) {
      const sized = media.replace(/<svg\b/i, `<svg width="${size}" height="${size}"`)
      return (
        <span
          className={className}
          aria-label={glyphLabel(name)}
          title={glyphLabel(name)}
          style={{ display: 'inline-flex', width: size, height: size, verticalAlign: 'middle', lineHeight: 0 }}
          dangerouslySetInnerHTML={{ __html: sized }}
        />
      )
    }
    return (
      <img
        src={media}
        alt={glyphLabel(name)}
        title={glyphLabel(name)}
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', display: 'inline-block', verticalAlign: 'middle' }}
      />
    )
  }
  return <>{children}</>
}
